import { ActionIcon, Paper, Skeleton, Table, Text } from "@mantine/core";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";

import type { TicketZeitraum, Vorgangstyp } from "@msc/shared";

import { useKpiTicketKunden } from "../../../hooks/useKpiTickets.js";
import { TicketAuswahlModal, type TicketAuswahl } from "./TicketAuswahlModal.js";
import { TicketBlockKopf } from "./TicketBlockKopf.js";
import { ZahlKnopf } from "./ReaktionTabelle.js";

/**
 * SR-05b: Welche Kunden erzeugen das Volumen? Kundenfirma, aufklappbar zu ihren
 * Kundenabteilungen, mit demselben Zeitraum des Vorjahres. Jede Zahl öffnet die
 * Tickets dahinter.
 */
export function KundenTabelle({ segmentId, year }: { segmentId: string; year: number }) {
  const { t } = useTranslation();
  const [vorgangstyp, setVorgangstyp] = useState<Vorgangstyp | null>(null);
  const [zeitraum, setZeitraum] = useState<TicketZeitraum | null>(null);
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [auswahl, setAuswahl] = useState<TicketAuswahl | null>(null);
  const { data, isLoading, isError } = useKpiTicketKunden(segmentId, year, zeitraum, vorgangstyp);

  const zeige = (name: string, abteilungIds: string[], vorjahr: boolean) => {
    if (!data) return;
    const g = vorjahr ? data.vorjahrZeitraum : data.zeitraum;
    setAuswahl({
      titel: t(vorjahr ? "pages.kpi.tickets.kunden.auswahlVorjahr" : "pages.kpi.tickets.kunden.auswahl", { kunde: name }),
      abschnitte: abteilungIds.map((abteilungId) => ({
        titel: data.firmen.flatMap((f) => f.abteilungen).find((a) => a.abteilungId === abteilungId)?.abteilung ?? abteilungId,
        abfrage: { quelle: "erstellt", ...g, vorgangstyp, abteilungId },
      })),
    });
  };
  const umschalten = (firma: string) =>
    setOffen((s) => {
      const n = new Set(s);
      if (n.has(firma)) n.delete(firma);
      else n.add(firma);
      return n;
    });

  return (
    <Paper p="md" withBorder>
      <TicketBlockKopf titel={t("pages.kpi.tickets.kunden.titel")} spiegel={data?.spiegel} vorgangstyp={vorgangstyp} onVorgangstyp={setVorgangstyp} zeitraum={zeitraum} onZeitraum={setZeitraum} />
      {isLoading ? (
        <Skeleton height={200} />
      ) : isError || !data ? (
        <Text size="sm" c="red">{t("pages.kpi.tickets.fehler")}</Text>
      ) : data.firmen.length === 0 ? (
        <Text size="sm" c="dimmed">{t("pages.kpi.tickets.kunden.leer")}</Text>
      ) : (
        <Table.ScrollContainer minWidth={520}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 40 }} />
                <Table.Th>{t("pages.kpi.tickets.kunden.colKunde")}</Table.Th>
                <Table.Th>{t("pages.kpi.tickets.kunden.colAnzahl")}</Table.Th>
                <Table.Th>{t("pages.kpi.tickets.kunden.colVorjahr")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.firmen.map((f) => {
                const ids = f.abteilungen.map((a) => a.abteilungId);
                const auf = offen.has(f.firma);
                return (
                  <Fragment key={f.firma}>
                    <Table.Tr>
                      <Table.Td>
                        {f.abteilungen.length > 1 && (
                          <ActionIcon variant="subtle" size="sm" aria-expanded={auf} aria-label={t(auf ? "pages.kpi.tickets.kunden.zuklappen" : "pages.kpi.tickets.kunden.aufklappen", { kunde: f.firma })} onClick={() => umschalten(f.firma)}>
                            {auf ? "−" : "+"}
                          </ActionIcon>
                        )}
                      </Table.Td>
                      <Table.Td fw={600}>{f.firma}</Table.Td>
                      <Table.Td><ZahlKnopf wert={f.anzahl} label={t("pages.kpi.tickets.kunden.knopf", { kunde: f.firma })} onClick={() => zeige(f.firma, ids, false)} /></Table.Td>
                      <Table.Td><ZahlKnopf wert={f.vorjahr} label={t("pages.kpi.tickets.kunden.knopfVorjahr", { kunde: f.firma })} onClick={() => zeige(f.firma, ids, true)} /></Table.Td>
                    </Table.Tr>
                    {auf && f.abteilungen.length > 1 &&
                      f.abteilungen.map((a) => (
                        <Table.Tr key={a.abteilungId}>
                          <Table.Td />
                          <Table.Td pl="xl">{a.abteilung}</Table.Td>
                          <Table.Td><ZahlKnopf wert={a.anzahl} label={t("pages.kpi.tickets.kunden.knopf", { kunde: a.abteilung })} onClick={() => zeige(a.abteilung, [a.abteilungId], false)} /></Table.Td>
                          <Table.Td><ZahlKnopf wert={a.vorjahr} label={t("pages.kpi.tickets.kunden.knopfVorjahr", { kunde: a.abteilung })} onClick={() => zeige(a.abteilung, [a.abteilungId], true)} /></Table.Td>
                        </Table.Tr>
                      ))}
                  </Fragment>
                );
              })}
              <Table.Tr style={{ borderTop: "2px solid var(--mantine-color-gray-4)" }}>
                <Table.Td />
                <Table.Td fw={700}>{t("pages.kpi.tickets.summe")}</Table.Td>
                <Table.Td fw={700}>{data.summe.anzahl}</Table.Td>
                <Table.Td fw={700}>{data.summe.vorjahr}</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <TicketAuswahlModal segmentId={segmentId} auswahl={auswahl} jiraBaseUrl={data?.jiraBaseUrl ?? null} onClose={() => setAuswahl(null)} />
    </Paper>
  );
}
